import * as React from 'react';

import dawnLogoFull from '../../assets/images/dawn-logo-full.svg';
import loginCircles from '../../assets/images/login-circles.png';
import { cn } from '../../lib/utils';

export interface AuthLayoutProps {
  /** Content to render in the center of the layout (typically a login/signup card) */
  children: React.ReactNode;
  /** Tagline text displayed below the logo */
  tagline?: string;
  /** Additional CSS classes for the container */
  className?: string;
  /** Whether to show the logo (default: true) */
  showLogo?: boolean;
  /** Whether to show the background pattern (default: true) */
  showPattern?: boolean;
}

/**
 * AuthLayout Template
 *
 * A full-screen layout for authentication pages with:
 * - Gradient/pattern background using login-circles.png
 * - Cream background color (#FFFAE9)
 * - Logo placement (top-left)
 * - Tagline text
 * - Centered content slot for login card
 *
 * @example
 * ```tsx
 * <AuthLayout tagline="Sign in to continue to the Dawn Security Platform.">
 *   <LoginForm />
 * </AuthLayout>
 * ```
 */
export const AuthLayout = React.forwardRef<HTMLDivElement, AuthLayoutProps>(
  (
    {
      children,
      tagline = 'Sign in to continue to the Dawn Security Platform.',
      className,
      showLogo = true,
      showPattern = true,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'min-h-screen w-full bg-dawn-cream relative overflow-hidden font-outfit',
          className
        )}
      >
        {/* Background circles pattern - full screen */}
        {showPattern && (
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              backgroundImage: `url(${loginCircles})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center right',
            }}
          />
        )}

        {/* Two-column layout centered in viewport with transparent wrapper */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-dawn-white/60 border-2 border-solid border-dawn-white rounded-lg p-10 flex gap-[100px] items-start">
          {/* Logo and tagline - left column */}
          {showLogo && (
            <div className="flex flex-col gap-6 shrink-0">
              <img src={dawnLogoFull} alt="Dawn Security" className="w-[195px] h-[54px]" />
              {tagline && (
                <p className="font-normal text-[13px] leading-[1.3] text-dawn-black w-[205px]">
                  {tagline}
                </p>
              )}
            </div>
          )}

          {/* Content slot - right column */}
          {children}
        </div>
      </div>
    );
  }
);

AuthLayout.displayName = 'AuthLayout';

export default AuthLayout;
